import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText, Mail, Search, Building, Loader2, Calendar, Download, ExternalLink, Eye,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';

const contractStatusBadge = (c) => {
  if (c.status === 'Expired') return <Badge variant="destructive">Expired</Badge>;
  if (c.status === 'Terminated') return <Badge variant="secondary">Terminated</Badge>;
  if (c.is_expiring_soon) return <Badge className="bg-amber-100 text-amber-800 border-amber-300">Expiring</Badge>;
  if (c.status === 'Active') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Active</Badge>;
  return <Badge variant="outline">{c.status}</Badge>;
};

const letterStatusBadge = (l) => {
  if (l.is_overdue) return <Badge variant="destructive">Overdue</Badge>;
  if (l.status === 'Responded') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Responded</Badge>;
  if (l.status === 'Pending Response') return <Badge className="bg-blue-100 text-blue-800 border-blue-300">Pending</Badge>;
  return <Badge variant="outline">{l.status || '—'}</Badge>;
};

const AllDocuments = () => {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Route guard: this cross-office ledger is super-admin only
  if (!isSuperAdmin()) {
    return <Navigate to="/overview" replace />;
  }

  const [tab, setTab] = useState('contracts');
  const [contracts, setContracts] = useState([]);
  const [letters, setLetters] = useState([]);
  const [offices, setOffices] = useState([]);
  const [search, setSearch] = useState('');
  const [officeFilter, setOfficeFilter] = useState('all');
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [loadingLetters, setLoadingLetters] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewKind, setPreviewKind] = useState(null); // 'contract' | 'letter'
  const [previewItem, setPreviewItem] = useState(null);

  const fetchOffices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/offices/`, { headers: getAuthHeaders() });
      if (res.ok) setOffices(await res.json());
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeaders]);

  const fetchContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const headers = getAuthHeaders();
      // Super admin: omit X-Office-ID for cross-office. Apply office filter via header for narrowing.
      if (officeFilter !== 'all') headers['X-Office-ID'] = officeFilter;
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '100');
      const res = await fetch(`${API_URL}/api/contracts/?${params.toString()}`, { headers });
      if (res.ok) {
        setContracts(await res.json());
      } else {
        toast.error('Failed to load contracts');
      }
    } catch (e) { console.error(e); }
    finally { setLoadingContracts(false); }
  }, [API_URL, getAuthHeaders, officeFilter, search]);

  const fetchLetters = useCallback(async () => {
    setLoadingLetters(true);
    try {
      const headers = getAuthHeaders();
      if (officeFilter !== 'all') headers['X-Office-ID'] = officeFilter;
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '100');
      const res = await fetch(`${API_URL}/api/letters/?${params.toString()}`, { headers });
      if (res.ok) {
        setLetters(await res.json());
      } else {
        toast.error('Failed to load letters');
      }
    } catch (e) { console.error(e); }
    finally { setLoadingLetters(false); }
  }, [API_URL, getAuthHeaders, officeFilter, search]);

  useEffect(() => { fetchOffices(); }, [fetchOffices]);
  useEffect(() => { fetchContracts(); fetchLetters(); }, [fetchContracts, fetchLetters]);

  const downloadContractPdf = async (contract) => {
    setDownloadingId(contract.id);
    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API_URL}/api/contracts/${contract.id}/attachment`, { headers });
      if (!res.ok) { toast.error('No attachment'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contract_${contract.contract_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) { toast.error('Download failed'); }
    finally { setDownloadingId(null); }
  };

  const downloadLetterAttachment = async (letter) => {
    setDownloadingId(letter.id);
    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API_URL}/api/letters/${letter.id}/attachment`, { headers });
      if (!res.ok) { toast.error('No attachment'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Letter_${letter.reference_number || letter.id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) { toast.error('Download failed'); }
    finally { setDownloadingId(null); }
  };

  const summary = useMemo(() => ({
    contracts: contracts.length,
    contractsExpiring: contracts.filter((c) => c.is_expiring_soon).length,
    letters: letters.length,
    lettersOverdue: letters.filter((l) => l.is_overdue).length,
  }), [contracts, letters]);

  return (
    <div className="p-6 space-y-6" data-testid="all-documents-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-600" />
          Documents Ledger
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cross-office view of all contracts and letters across the organisation.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Total Contracts</div>
          <div className="text-2xl font-bold">{summary.contracts}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Contracts Expiring</div>
          <div className="text-2xl font-bold text-amber-600">{summary.contractsExpiring}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Total Letters</div>
          <div className="text-2xl font-bold">{summary.letters}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Letters Overdue</div>
          <div className="text-2xl font-bold text-red-600">{summary.lettersOverdue}</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contracts and letters..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="docs-search"
            />
          </div>
          <Select value={officeFilter} onValueChange={setOfficeFilter}>
            <SelectTrigger className="w-[220px]" data-testid="docs-office-filter">
              <SelectValue placeholder="Filter by office" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All offices</SelectItem>
              {offices.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="contracts" data-testid="tab-contracts">
            <FileText className="h-4 w-4 mr-2" /> Contracts ({summary.contracts})
          </TabsTrigger>
          <TabsTrigger value="letters" data-testid="tab-letters">
            <Mail className="h-4 w-4 mr-2" /> Letters ({summary.letters})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contracts">
          <Card>
            <CardContent className="p-0">
              {loadingContracts ? (
                <div className="p-12 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading contracts...
                </div>
              ) : contracts.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No contracts match your filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-medium">Contract #</th>
                        <th className="px-4 py-3 font-medium">Title / Party</th>
                        <th className="px-4 py-3 font-medium">Office</th>
                        <th className="px-4 py-3 font-medium">Type</th>
                        <th className="px-4 py-3 font-medium">End Date</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contracts.map((c) => (
                        <tr key={c.id} className="border-t hover:bg-muted/20" data-testid={`contract-row-${c.id}`}>
                          <td className="px-4 py-3 font-mono text-xs">{c.contract_number}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{c.title}</div>
                            <div className="text-xs text-muted-foreground">{c.party_name}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs">
                              <Building className="h-3 w-3 opacity-60" />
                              {c.office_name || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">{c.contract_type}</td>
                          <td className="px-4 py-3 text-xs">
                            <div className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 opacity-60" />{c.end_date}</div>
                            {typeof c.days_until_expiry === 'number' && c.status === 'Active' && (
                              <div className={`text-[11px] mt-0.5 ${c.days_until_expiry < 0 ? 'text-red-600' : c.is_expiring_soon ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                {c.days_until_expiry < 0 ? `${Math.abs(c.days_until_expiry)} days ago` : `in ${c.days_until_expiry} days`}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">{contractStatusBadge(c)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {c.has_attachment && (
                                <>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => { setPreviewKind('contract'); setPreviewItem(c); }}
                                    title="Preview PDF"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => downloadContractPdf(c)}
                                    disabled={downloadingId === c.id}
                                    title="Download PDF"
                                  >
                                    {downloadingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => window.open('/contracts', '_self')}
                                title="Open in Contracts page"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="letters">
          <Card>
            <CardContent className="p-0">
              {loadingLetters ? (
                <div className="p-12 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading letters...
                </div>
              ) : letters.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No letters match your filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-medium">Reference</th>
                        <th className="px-4 py-3 font-medium">Subject</th>
                        <th className="px-4 py-3 font-medium">Office</th>
                        <th className="px-4 py-3 font-medium">Direction</th>
                        <th className="px-4 py-3 font-medium">From / To</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {letters.map((l) => (
                        <tr key={l.id} className="border-t hover:bg-muted/20" data-testid={`letter-row-${l.id}`}>
                          <td className="px-4 py-3 font-mono text-xs">{l.reference_number}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{l.subject}</div>
                            <div className="text-xs text-muted-foreground">{l.letter_date}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs">
                              <Building className="h-3 w-3 opacity-60" />
                              {l.office_name || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">{l.direction}</td>
                          <td className="px-4 py-3 text-xs">
                            {l.direction === 'Incoming' ? (l.sender || '—') : (l.recipient || '—')}
                          </td>
                          <td className="px-4 py-3">{letterStatusBadge(l)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {l.has_attachment && (
                                <>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => { setPreviewKind('letter'); setPreviewItem(l); }}
                                    title="Preview attachment"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => downloadLetterAttachment(l)}
                                    disabled={downloadingId === l.id}
                                    title="Download attachment"
                                  >
                                    {downloadingId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => window.open('/letters', '_self')}
                                title="Open in Letters page"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AttachmentPreviewModal
        open={!!previewItem}
        onClose={() => { setPreviewItem(null); setPreviewKind(null); }}
        title={previewKind === 'contract'
          ? `Contract ${previewItem?.contract_number || ''} - ${previewItem?.title || ''}`
          : `Letter ${previewItem?.reference_number || ''} - ${previewItem?.subject || ''}`}
        downloadName={previewKind === 'contract'
          ? `Contract_${previewItem?.contract_number || previewItem?.id}.pdf`
          : `Letter_${previewItem?.reference_number || previewItem?.id}.pdf`}
        fetchUrl={previewItem
          ? `${API_URL}/api/${previewKind === 'contract' ? 'contracts' : 'letters'}/${previewItem.id}/attachment`
          : ''}
        headers={getAuthHeaders()}
      />
    </div>
  );
};

export default AllDocuments;
